// src/components/TransitionCard.tsx

import React, { useMemo, memo } from 'react';
import { easeCubicInOut } from 'd3-ease';

// Helper function to wrap text, just like in D3Cloud
function wrapText(context: CanvasRenderingContext2D, text: string, maxWidth: number, lineHeight: number): string[] {
    const words = text.split(' ');
    let line = '';
    const lines = [];
    for (let n = 0; n < words.length; n++) {
        const testLine = line + words[n] + ' ';
        const metrics = context.measureText(testLine);
        if (metrics.width > maxWidth && n > 0) {
            lines.push(line);
            line = words[n] + ' ';
        } else {
            line = testLine;
        }
    }
    lines.push(line);
    return lines;
}

interface TransitionCardProps {
  text: string;
  isVisible: boolean;
  progress?: number;
  displayStyle?: 'fly-by' | 'fade-in-out';
  duration?: number;
}

const TransitionCard: React.FC<TransitionCardProps> = ({
  text, isVisible, progress = 0, displayStyle = 'fly-by', duration = 1200,
}) => {
    const isMobile = useMemo(() => window.innerWidth < 768, []);

    // <<< Styles now perfectly match D3Cloud's drawTitleCard >>>
    const cardWidth = isMobile ? 320 : 615;
    const cardHeight = isMobile ? 80 : 60;
    const fontStyle = isMobile ? '500 28px "Inter", sans-serif' : '500 34px "Inter", sans-serif';
    const lineHeight = isMobile ? 32 : 40;

    const animationStyle = useMemo(() => {
        let opacity = 0;
        let transform = `translate(-50%, ${window.innerHeight / 2 - cardHeight}px)`;

        if (isVisible) {
            if (displayStyle === 'fly-by') {
                const startY = -cardHeight;
                const endY = window.innerHeight;
                const currentY = startY + (endY - startY) * easeCubicInOut(progress);
                opacity = 1;
                transform = `translate(-50%, ${currentY}px)`;
            } else {
                opacity = 1;
            }
        }
        return { opacity, transform, transition: displayStyle === 'fade-in-out' ? `opacity ${duration / 2}ms ease-in-out` : 'none' };
    }, [isVisible, progress, displayStyle, duration, cardHeight]);

    const cardStyle: React.CSSProperties = {
        position: 'fixed', top: 0, left: '50%',
        width: `${cardWidth}px`, height: `${cardHeight}px`,
        backgroundColor: 'rgba(255, 255, 255, 0.58)',
        borderRadius: `4px`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        textAlign: 'center', // <<< ADDED
        padding: '0 20px',   // <<< ADDED
        font: fontStyle,     // <<< DYNAMIC
        color: "#1A1A1A", zIndex: 1000, pointerEvents: 'none',
        visibility: animationStyle.opacity > 0 ? 'visible' : 'hidden',
        ...animationStyle,
    };

    return <div style={cardStyle}>{text}</div>;
};

export default memo(TransitionCard);